import { AlertCircle, RotateCcw, Trash2, Info } from 'lucide-react';

/**
 * Modal som tilbys brukeren når vi finner et lokalt utkast som er nyere
 * enn siste lagrede versjon i DB (task #300, 2026-07-02).
 *
 * Vises kun ved side-load — brukeren velger eksplisitt å gjenopprette
 * eller forkaste. Ingen automatisk overskriving.
 */

interface RecoverDraftModalProps {
  /** Timestamp for da draften ble lagret (ms siden epoch) */
  draftTimestamp: number;
  /** ISO-timestamp for siste lagrede DB-versjon (null hvis ny plan) */
  dbUpdatedAt: string | null;
  /** Kalles når bruker klikker "Gjenopprett" */
  onRecover: () => void;
  /** Kalles når bruker klikker "Forkast" */
  onDiscard: () => void;
}

function formatRelativeTime(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSec < 60) return 'for få sekunder siden';
  if (diffMin < 60) return `for ${diffMin} minutt${diffMin === 1 ? '' : 'er'} siden`;
  if (diffHours < 24) return `for ${diffHours} time${diffHours === 1 ? '' : 'r'} siden`;
  if (diffDays < 30) return `for ${diffDays} dag${diffDays === 1 ? '' : 'er'} siden`;
  return new Date(timestamp).toLocaleString('nb-NO');
}

export default function RecoverDraftModal({
  draftTimestamp,
  dbUpdatedAt,
  onRecover,
  onDiscard,
}: RecoverDraftModalProps) {
  const isNewPlan = !dbUpdatedAt;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-amber-50 border-b border-amber-200 p-4 flex items-start gap-3">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
          <div>
            <h2 className="text-lg font-bold text-amber-900">Ulagret utkast funnet</h2>
            <p className="text-sm text-amber-800 mt-1">
              Nettleseren har et lokalt utkast som ikke er lagret til Kompetanseforbundet.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm space-y-1">
            <div className="flex justify-between text-gray-700">
              <span>Utkast sist endret:</span>
              <strong>{formatRelativeTime(draftTimestamp)}</strong>
            </div>
            {!isNewPlan && (
              <div className="flex justify-between text-gray-700">
                <span>Siste lagrede versjon:</span>
                <strong>{new Date(dbUpdatedAt!).toLocaleString('nb-NO')}</strong>
              </div>
            )}
            {isNewPlan && (
              <div className="flex justify-between text-gray-500 italic">
                <span>Denne planen er ikke lagret ennå</span>
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 text-sm text-kf-blue-900 bg-kf-blue-50 border border-kf-blue-200 rounded p-3">
            <Info size={16} className="flex-shrink-0 mt-0.5 text-kf-blue-600" />
            <div>
              <p>
                Utkastet inneholder alle endringer du gjorde siden sist du klikket «Lagre utkast» —
                inkludert KI-genererte utbytter, ESCO-koblinger og manuelle justeringer.
              </p>
              <p className="mt-2">
                <strong>Gjenopprett</strong> for å fortsette der du slapp. <strong>Forkast</strong> for å
                laste inn den lagrede versjonen i stedet (utkastet slettes fra nettleseren).
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 p-3 flex justify-end gap-2 bg-gray-50">
          <button
            type="button"
            onClick={onDiscard}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100"
          >
            <Trash2 size={16} />
            Forkast utkast
          </button>
          <button
            type="button"
            onClick={onRecover}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-kf-primary rounded hover:opacity-90"
          >
            <RotateCcw size={16} />
            Gjenopprett utkast
          </button>
        </div>
      </div>
    </div>
  );
}
